import moment from 'moment';

const BASE_RADIUS_KM = 50;
const MAX_GAP_HOURS = 48;
const MIN_EVENTS = 3;
const MAX_DATA_GAP_HOURS = 72;
const RECENT_DAYS_FOR_BASE = 90;

const toRad = (deg) => (deg * Math.PI) / 180;

const haversineKm = (lat1, lon1, lat2, lon2) => {
  if (
    lat1 == null || lon1 == null || lat2 == null || lon2 == null
    || Number.isNaN(Number(lat1)) || Number.isNaN(Number(lon1))
    || Number.isNaN(Number(lat2)) || Number.isNaN(Number(lon2))
  ) {
    return null;
  }
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
    * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const centroid = (points) => {
  if (!points || points.length === 0) return { lat: 0, lon: 0 };
  const sumLat = points.reduce((s, p) => s + (Number(p.lat) || 0), 0);
  const sumLon = points.reduce((s, p) => s + (Number(p.lon) || 0), 0);
  return { lat: sumLat / points.length, lon: sumLon / points.length };
};

export const extractDateStr = (item) => {
  const candidates = [item.date, item.entry_date, item.watched_at, item.event_date, item.route_date];
  for (const d of candidates) {
    if (d) return String(d).slice(0, 10);
  }
  return null;
};

export const detectHomeBase = (data = {}, { recentDays = RECENT_DAYS_FOR_BASE } = {}) => {
  const cutoff = moment().subtract(recentDays, 'days');
  const cityCounts = new Map();

  (data.checkins || []).forEach(c => {
    if (!c.date || !c.city) return;
    if (moment(c.date).isBefore(cutoff)) return;
    const key = `${c.lat?.toFixed(4) || '0'},${c.lon?.toFixed(4) || '0'}`;
    const count = cityCounts.get(key);
    if (!count || (c.times_visited || 1) > count.times_visited) {
      cityCounts.set(key, {
        lat: Number(c.lat),
        lon: Number(c.lon),
        city: c.city.trim(),
        country: c.country || '',
        times_visited: c.times_visited || 1,
      });
    }
  });

  if (cityCounts.size === 0) return { lat: null, lon: null, city: '', country: '' };

  const sorted = [...cityCounts.values()].sort((a, b) => b.times_visited - a.times_visited);
  return sorted[0];
};

export const extractTripEvents = (data = {}, { homeBase } = {}) => {
  const base = homeBase || detectHomeBase(data);
  const events = [];

  (data.checkins || []).forEach(c => {
    if (!c.date || c.lat == null || c.lon == null) return;
    const dist = haversineKm(base.lat, base.lon, Number(c.lat), Number(c.lon));
    if (dist === null || dist < BASE_RADIUS_KM) return;
    events.push({
      source: 'checkin',
      date: extractDateStr(c),
      lat: Number(c.lat),
      lon: Number(c.lon),
      city: c.city || '',
      country: c.country || '',
      venue: c.venue || '',
      raw: c,
    });
  });

  const addRouteEvents = (routes, sourceKey) => {
    (routes || []).forEach(route => {
      const points = Array.isArray(route) ? route : [route];
      if (points.length === 0) return;
      const pts = points.map(p => ({ lat: Number(p.lat), lon: Number(p.lon) })).filter(p => p.lat && p.lon);
      if (pts.length === 0) return;
      const c = centroid(pts);
      const dist = haversineKm(base.lat, base.lon, c.lat, c.lon);
      if (dist === null || dist < BASE_RADIUS_KM) return;
      const firstPoint = points[0];
      const routeDate = firstPoint.route_date || firstPoint.date;
      const routeType = firstPoint.route_type || sourceKey;
      events.push({
        source: sourceKey,
        date: routeDate ? String(routeDate).slice(0, 10) : null,
        lat: c.lat,
        lon: c.lon,
        city: '',
        country: '',
        venue: '',
        routeType,
        raw: route,
      });
    });
  };

  addRouteEvents(data.health, 'health');
  addRouteEvents(data.moves, 'moves');
  addRouteEvents(data.googleMaps, 'google_maps');

  (data.concerts || []).forEach(c => {
    if (!c.event_date) return;
    const lat = Number(c.latitude);
    const lon = Number(c.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;
    const dist = haversineKm(base.lat, base.lon, lat, lon);
    if (dist === null || dist < BASE_RADIUS_KM) return;
    events.push({
      source: 'concerts',
      date: String(c.event_date).slice(0, 10),
      lat,
      lon,
      city: c.city_name || '',
      country: c.country_name || '',
      venue: c.venue_name || '',
      raw: c,
    });
  });

  events.sort((a, b) => {
    const da = a.date || '';
    const db = b.date || '';
    return da.localeCompare(db) || a.source.localeCompare(b.source);
  });

  return { base, events };
};

export const groupTrips = (events = [], { maxGapHours = MAX_GAP_HOURS, maxDataGapHours = MAX_DATA_GAP_HOURS } = {}) => {
  if (events.length === 0) return [];

  const trips = [];
  let current = {
    events: [events[0]],
    startDate: events[0].date,
    endDate: events[0].date,
  };

  for (let i = 1; i < events.length; i++) {
    const prev = events[i - 1];
    const curr = events[i];

    const prevMoment = moment(prev.date);
    const currMoment = moment(curr.date);
    if (!prevMoment.isValid() || !currMoment.isValid()) {
      current.events.push(curr);
      current.endDate = curr.date || current.endDate;
      continue;
    }

    const gapHours = moment.duration(currMoment.diff(prevMoment)).asHours();

    if (gapHours <= maxGapHours) {
      current.events.push(curr);
      current.endDate = curr.date;
      continue;
    }

    const lastEventInCurrent = current.events[current.events.length - 1];
    const lastMoment = moment(lastEventInCurrent.date);
    const gapFromLast = moment.duration(currMoment.diff(lastMoment)).asHours();

    if (gapFromLast <= maxDataGapHours) {
      current.events.push(curr);
      current.endDate = curr.date;
      continue;
    }

    trips.push(current);
    current = {
      events: [curr],
      startDate: curr.date,
      endDate: curr.date,
    };
  }

  trips.push(current);

  return trips
    .filter(trip => trip.events.length >= MIN_EVENTS)
    .map((trip, index) => buildTripSummary(trip, index));
};

export const buildTripSummary = (rawTrip, index) => {
  const { events } = rawTrip;

  const cities = new Map();
  const sources = new Set();
  const lats = [];
  const lons = [];

  events.forEach(e => {
    if (e.city) {
      const key = e.city.trim().toLowerCase();
      if (!cities.has(key)) cities.set(key, { city: e.city.trim(), country: e.country || '', count: 0 });
      cities.get(key).count += 1;
    }
    sources.add(e.source);
    if (e.lat != null && e.lon != null) {
      lats.push(e.lat);
      lons.push(e.lon);
    }
  });

  const sortedCities = [...cities.values()].sort((a, b) => b.count - a.count);
  const mainCity = sortedCities.length > 0 ? sortedCities[0].city : '';
  const mainCountry = sortedCities.length > 0 ? sortedCities[0].country : '';

  const sourceCounts = {};
  events.forEach(e => {
    const s = e.source;
    sourceCounts[s] = (sourceCounts[s] || 0) + 1;
    if (e.routeType && e.source !== e.routeType) {
      const rtKey = `${e.source}:${e.routeType}`;
      sourceCounts[rtKey] = (sourceCounts[rtKey] || 0) + 1;
    }
  });

  const bounds = lats.length >= 2
    ? {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons),
    }
    : lats.length === 1
    ? {
      minLat: lats[0] - 0.5,
      maxLat: lats[0] + 0.5,
      minLon: lons[0] - 0.5,
      maxLon: lons[0] + 0.5,
    }
    : null;

  return {
    id: `trip-${index}`,
    title: mainCity || `Viagem ${index + 1}`,
    mainCity,
    mainCountry,
    startDate: rawTrip.startDate,
    endDate: rawTrip.endDate,
    events,
    cities: sortedCities,
    sources: [...sources],
    sourceCounts,
    bounds,
  };
};

export const detectTrips = (data = {}, options = {}) => {
  const { base, events } = extractTripEvents(data, options);
  const trips = groupTrips(events, options);
  return { base, events, trips };
};
